// Phase 44.31.4 — Admin · Driver Pilot Onboarding
// Selezione driver pilot (max 20), invio email Resend onboarding,
// tabella readiness con metriche operative.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Send, Loader2, RefreshCw, CheckCircle2, AlertCircle, Circle,
  Smartphone, MapPin, Route as RouteIcon, Mail, Users, ShieldCheck,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const MAX_SELECTION = 20;

function relativeTimeShort(iso) {
  if (!iso) return "mai";
  try {
    const d = new Date(iso);
    const s = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
    if (s < 60) return "ora";
    if (s < 3600) return `${Math.floor(s / 60)} min fa`;
    if (s < 86400) return `${Math.floor(s / 3600)} h fa`;
    if (s < 30 * 86400) return `${Math.floor(s / 86400)} g fa`;
    return new Intl.DateTimeFormat("it-CH", { dateStyle: "short" }).format(d);
  } catch { return "—"; }
}

const STATUS_META = {
  ready:   { label: "Pronto",  cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  partial: { label: "Parziale", cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: AlertCircle },
  blocked: { label: "Bloccato", cls: "bg-red-50 text-red-700 border-red-200", Icon: Circle },
};

const ONLINE_META = {
  online:  { label: "online",  cls: "text-emerald-700", dot: "bg-emerald-500" },
  idle:    { label: "idle",    cls: "text-amber-700",   dot: "bg-amber-500" },
  offline: { label: "offline", cls: "text-zinc-500",    dot: "bg-zinc-300" },
};

export default function AdminDriverPilot() {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [readiness, setReadiness] = useState({ items: [], aggregates: null });
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastSend, setLastSend] = useState(null);
  const [search, setSearch] = useState("");

  const loadCandidates = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/driver-pilot/candidates");
      setCandidates(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento driver");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCandidates(); }, []);

  const loadReadiness = async (ids) => {
    if (!ids || ids.length === 0) {
      setReadiness({ items: [], aggregates: null });
      return;
    }
    setReadinessLoading(true);
    try {
      const { data } = await api.get(`/admin/driver-pilot/readiness?ids=${ids.join(",")}`);
      setReadiness({ items: data.items || [], aggregates: data.aggregates || null });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore readiness");
    } finally {
      setReadinessLoading(false);
    }
  };

  // Re-fetch readiness ogni volta che cambia la selezione
  useEffect(() => {
    loadReadiness(Array.from(selected));
    // eslint-disable-next-line
  }, [selected]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((c) =>
      (c.name || "").toLowerCase().includes(q)
      || (c.email || "").toLowerCase().includes(q)
      || (c.vehicle_plate || "").toLowerCase().includes(q)
    );
  }, [candidates, search]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else {
        if (next.size >= MAX_SELECTION) {
          toast.warning(`Massimo ${MAX_SELECTION} driver per invio. Deseleziona uno per aggiungere.`);
          return prev;
        }
        next.add(id);
      }
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const sendEmail = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) {
      toast.warning("Seleziona almeno un driver");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/driver-pilot/onboarding-email", { driver_ids: ids });
      const s = data?.summary || {};
      setLastSend(data);
      const msg = `Email inviate: ${s.sent || 0}/${ids.length}` + (s.failed ? ` · ${s.failed} fallite` : "");
      if (s.failed || s.not_found) toast.warning(msg);
      else toast.success(msg);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore invio email");
    } finally {
      setSending(false);
    }
  };

  const agg = readiness.aggregates;

  return (
    <div className="space-y-6 overflow-x-hidden" data-testid="admin-driver-pilot-page">
      <header>
        <p className="overline">Admin · prep pilot</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Driver Pilot</h1>
        <p className="text-sm text-dapnow-muted mt-1 leading-relaxed">
          Seleziona i driver (max {MAX_SELECTION}) che parteciperanno al pilot, inviagli l'email di onboarding
          Resend (install PWA, push, GPS) e verifica in tempo reale lo stato di readiness operativa.
        </p>
      </header>

      {/* Aggregati readiness */}
      {agg && (
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="readiness-aggregates">
          <Stat label="Pronti" value={agg.ready} accent="emerald" icon={CheckCircle2} />
          <Stat label="Push attive" value={agg.with_push} suffix={`/${agg.total}`} accent="blue" icon={Smartphone} />
          <Stat label="GPS salvato" value={agg.with_gps} suffix={`/${agg.total}`} accent="violet" icon={MapPin} />
          <Stat label="Online" value={agg.online} suffix={`/${agg.total}`} accent="emerald" icon={Users} />
        </section>
      )}

      {/* Barra azioni */}
      <section className="rounded-2xl border border-dapnow-line bg-white p-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome, email, targa…"
            data-testid="driver-pilot-search"
            className="flex-1 min-w-[200px] h-10 rounded-xl border border-dapnow-line bg-white px-3 text-sm"
          />
          <button
            type="button"
            onClick={loadCandidates}
            className="inline-flex items-center gap-1.5 h-10 px-3 rounded-xl border border-dapnow-line bg-white text-xs font-semibold hover:border-dapnow-ink"
            data-testid="driver-pilot-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Aggiorna
          </button>
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-dapnow-muted">
            <strong className="text-dapnow-ink">{selected.size}</strong> di {MAX_SELECTION} selezionati
            {selected.size > 0 && (
              <button onClick={clearSelection} className="ml-2 text-dapnow-blue hover:underline" data-testid="driver-pilot-clear">
                Deseleziona tutti
              </button>
            )}
          </p>
          <button
            type="button"
            onClick={sendEmail}
            disabled={sending || selected.size === 0}
            data-testid="driver-pilot-send-btn"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-dapnow-blue text-white text-sm font-bold hover:opacity-90 disabled:opacity-50"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Invia email onboarding ({selected.size})
          </button>
        </div>
        {lastSend && (
          <div className="rounded-lg border border-dapnow-line bg-zinc-50 p-3 text-xs" data-testid="driver-pilot-last-send">
            <p className="font-semibold mb-1">Ultimo invio:</p>
            <p className="text-dapnow-muted">
              Inviate: <strong className="text-emerald-700">{lastSend.summary.sent}</strong> ·
              Fallite: <strong className="text-red-700">{lastSend.summary.failed}</strong> ·
              Mockate: <strong className="text-amber-700">{lastSend.summary.mocked}</strong>
              {lastSend.summary.not_found ? ` · Non trovate: ${lastSend.summary.not_found}` : ""}
            </p>
          </div>
        )}
      </section>

      {/* Lista candidati con readiness inline */}
      <section className="rounded-2xl border border-dapnow-line bg-white overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-dapnow-muted text-sm">Caricamento driver…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-dapnow-muted text-sm">Nessun driver trovato.</div>
        ) : (
          <ul data-testid="driver-pilot-candidates-list">
            {filtered.map((d) => {
              const r = readiness.items.find((x) => x.driver_id === d.id);
              const isSelected = selected.has(d.id);
              return (
                <li
                  key={d.id}
                  className={`p-3 sm:p-4 border-b border-dapnow-line last:border-b-0 flex items-start gap-3 ${isSelected ? "bg-dapnow-blue-soft/30" : ""}`}
                  data-testid={`driver-pilot-row-${d.id}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggle(d.id)}
                    data-testid={`driver-pilot-checkbox-${d.id}`}
                    className="mt-1 h-5 w-5 rounded border-dapnow-line accent-dapnow-blue shrink-0 cursor-pointer"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold tracking-tight text-sm">{d.name || "—"}</p>
                      {d.vehicle_type && (
                        <span className="text-[10px] uppercase font-semibold tracking-wider text-dapnow-muted bg-dapnow-bg px-1.5 py-0.5 rounded">
                          {d.vehicle_type}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-dapnow-muted truncate">{d.email}</p>
                    {isSelected && r && (
                      <ReadinessChips r={r} />
                    )}
                  </div>
                  {isSelected && r && (
                    <StatusBadge status={r.status} />
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {readinessLoading && (
          <div className="px-4 py-2 border-t border-dapnow-line text-[11px] text-dapnow-muted flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" /> Aggiorno readiness…
          </div>
        )}
      </section>

      {/* Help operativo */}
      <section className="rounded-2xl border border-dapnow-line bg-zinc-50/60 p-4 text-xs text-dapnow-muted leading-relaxed">
        <p className="font-semibold text-dapnow-ink mb-1">Come si legge il pannello</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Pronto</strong>: push attive + GPS salvato + almeno un tragitto pendolare.</li>
          <li><strong>Parziale</strong>: almeno uno dei tre presente — utile per pilot ma da completare.</li>
          <li><strong>Bloccato</strong>: nessuno dei tre → il driver non riceverà ordini marketplace.</li>
          <li><strong>Online</strong> = ultima sessione &lt; 15 min · <strong>Idle</strong> &lt; 1h · <strong>Offline</strong> oltre.</li>
        </ul>
      </section>
    </div>
  );
}

function Stat({ label, value, suffix, accent, icon: Icon }) {
  const accents = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    blue:    "border-blue-200 bg-blue-50 text-blue-700",
    violet:  "border-violet-200 bg-violet-50 text-violet-700",
    amber:   "border-amber-200 bg-amber-50 text-amber-800",
  };
  return (
    <div className={`rounded-2xl border p-4 ${accents[accent] || accents.blue}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="h-3.5 w-3.5" />
        <p className="overline text-[10px] font-bold">{label}</p>
      </div>
      <p className="font-display text-2xl font-extrabold">{value}<span className="text-base text-dapnow-muted font-normal">{suffix || ""}</span></p>
    </div>
  );
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.blocked;
  const Icon = meta.Icon;
  return (
    <span
      className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${meta.cls}`}
      data-testid={`driver-pilot-status-${status}`}
    >
      <Icon className="h-3 w-3" /> {meta.label}
    </span>
  );
}

function ReadinessChips({ r }) {
  const online = ONLINE_META[r.online_state] || ONLINE_META.offline;
  return (
    <div className="mt-2 flex items-center gap-2 flex-wrap text-[11px]">
      <span className={`inline-flex items-center gap-1 ${r.push_subs_active > 0 ? "text-blue-700" : "text-dapnow-muted line-through"}`}>
        <Smartphone className="h-3 w-3" /> push {r.push_subs_active}
      </span>
      <span className={`inline-flex items-center gap-1 ${r.has_gps ? "text-violet-700" : "text-dapnow-muted line-through"}`}>
        <MapPin className="h-3 w-3" /> GPS
      </span>
      <span className={`inline-flex items-center gap-1 ${r.commuter_routes > 0 ? "text-emerald-700" : "text-dapnow-muted line-through"}`}>
        <RouteIcon className="h-3 w-3" /> tragitti {r.commuter_routes}
      </span>
      <span className={`inline-flex items-center gap-1 ${online.cls}`}>
        <span className={`inline-block h-1.5 w-1.5 rounded-full ${online.dot}`} /> {online.label}
        {r.last_seen_at ? ` · ${relativeTimeShort(r.last_seen_at)}` : ""}
      </span>
    </div>
  );
}
